import axiosInstance from '@/integrations/axios/axios-instance'

export interface ServiceConfig {
  emailFetchTime: string // Format: "01:00" (24-hour format)
  notificationSendTime: string // Format: "08:00" (24-hour format)
  backupTime: string // Format: "02:00" (24-hour format)
}

export interface ServiceStatus {
  serviceName: 'email_fetch' | 'notification_send' | 'backup'
  scheduledTime: string
  actualRunTime?: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'missed'
  duration?: number
  processedCount?: number
  errorMessage?: string
}

export interface ServiceStatusResponse {
  recentServices: Array<ServiceStatus>
}

export interface ManualFetchResponse {
  emailsProcessed: number
  donationsCreated: number
  message: string
}

export interface ManualNotificationResponse {
  pendingDonations: number
  message: string
}

export interface RecoveryResponse {
  message: string
}

export interface FetchFromDateResponse {
  message: string
  processed: number
  skipped: number
  timestamp: string
}

export interface FetchByReferenceResult {
  referenceNumber: string
  status: 'found_in_db' | 'created' | 'not_found_in_gmail' | 'parse_failed'
  receiptNumber?: string
  donationId?: string
}

export interface FetchByReferenceResponse {
  results: FetchByReferenceResult[]
  timestamp?: string
}

export interface ConfigResponse {
  schedules: {
    emailFetch: string
    notifications: string
    backup: string
    emailFetchTime: string
    notificationSendTime: string
    backupTime: string
  }
  emailSettings: {
    targetSender: string
    timezone: string
    lookbackDays: number
  }
  features: {
    autoMemberMatching: boolean
    emailPDFGeneration: boolean
    duplicateDetection: boolean
  }
  lastUpdated: string
}

// API Functions
export const serviceConfigApi = {
  // Get current configuration
  getConfig: async (): Promise<ConfigResponse> => {
    const response = await axiosInstance.get('/auto-etransfer/config')
    return response.data
  },

  // Get service status
  getServiceStatus: async (): Promise<ServiceStatusResponse> => {
    const response = await axiosInstance.get('/auto-etransfer/service-status')
    return response.data
  },

  // Update configuration (schedules, settings, features)
  updateConfig: async (config: ServiceConfig): Promise<void> => {
    const response = await axiosInstance.post('/auto-etransfer/config', config)
    return response.data
  },

  // Manual fetch emails
  manualFetchEmails: async (): Promise<ManualFetchResponse> => {
    const response = await axiosInstance.post('/auto-etransfer/trigger-fetch')
    return response.data
  },

  // Manual send notifications
  manualSendNotifications: async (): Promise<ManualNotificationResponse> => {
    const response = await axiosInstance.post(
      '/auto-etransfer/manual-notification',
    )
    return response.data
  },

  // Trigger recovery
  triggerRecovery: async (): Promise<RecoveryResponse> => {
    const response = await axiosInstance.post(
      '/auto-etransfer/trigger-recovery',
    )
    return response.data
  },

  // Unmatch donations
  unmatchDonations: async (): Promise<RecoveryResponse> => {
    const response = await axiosInstance.post(
      '/auto-etransfer/notify-unmatched-donors',
    )
    return response.data
  },

  // Fetch all e-transfers from a specific date (developer recovery tool)
  fetchFromDate: async (fromDate: string): Promise<FetchFromDateResponse> => {
    const response = await axiosInstance.post('/auto-etransfer/fetch-from-date', { fromDate })
    return response.data
  },

  // Fetch one or more e-transfers by reference number (developer manual recovery)
  fetchByReference: async (referenceNumbers: string[]): Promise<FetchByReferenceResponse> => {
    const response = await axiosInstance.post('/auto-etransfer/fetch-by-reference', { referenceNumbers })
    return response.data
  },
}
